import { useState, useEffect, useRef, useCallback } from 'react';
import { settingsApi } from '../services/api';
import { Printer, Barcode, TextAa, Article, Trash, ArrowsClockwise, CheckCircle, XCircle, Warning } from '@phosphor-icons/react';

// ============================================================
// POS DIAGNOSTICS PANEL
// Frontend-only diagnostic tool. Does NOT touch production data.
// Goal: identify why POSPrinter (Android print service) fails
// on NB55 Handheld POS before attempting native ESC/POS.
// ============================================================

const LOG_KEY = 'parkdesk_pos_diag_log';
const MAX_LOG = 30;

const readLog = () => {
  try { return JSON.parse(localStorage.getItem(LOG_KEY) || '[]'); }
  catch { return []; }
};

const appendLog = (entry) => {
  const arr = readLog();
  arr.unshift(entry);
  const trimmed = arr.slice(0, MAX_LOG);
  try { localStorage.setItem(LOG_KEY, JSON.stringify(trimmed)); } catch {}
  return trimmed;
};

// Detect browser/device info safely (no PII)
const detectEnv = () => {
  const ua = navigator.userAgent || '';
  let browser = 'Unknown';
  let version = '';
  if (/Edg\//.test(ua)) { browser = 'Edge'; version = (ua.match(/Edg\/([\d.]+)/) || [])[1] || ''; }
  else if (/Chrome\//.test(ua)) { browser = 'Chrome'; version = (ua.match(/Chrome\/([\d.]+)/) || [])[1] || ''; }
  else if (/Firefox\//.test(ua)) { browser = 'Firefox'; version = (ua.match(/Firefox\/([\d.]+)/) || [])[1] || ''; }
  else if (/Safari\//.test(ua)) { browser = 'Safari'; version = (ua.match(/Version\/([\d.]+)/) || [])[1] || ''; }

  const androidMatch = ua.match(/Android\s+([\d.]+)/);
  const androidVersion = androidMatch ? androidMatch[1] : null;
  const isAndroid = /Android/.test(ua);
  const isMobile = /Mobi|Android|iPhone|iPad/.test(ua);

  // PWA standalone detection
  const standalone =
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true;

  return {
    user_agent: ua,
    browser,
    version,
    android: isAndroid,
    android_version: androidVersion,
    mobile: isMobile,
    pwa_standalone: standalone,
    web_bluetooth: !!(navigator.bluetooth && navigator.bluetooth.requestDevice),
    window_print: typeof window.print === 'function',
    screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    online: navigator.onLine,
    language: navigator.language || '',
  };
};

// ============== HTML TEMPLATES (3 progressive tests) ==============

const baseHtml = (bodyHtml, autoPrintMs = 400) => `<!DOCTYPE html><html><head><meta charset="utf-8"><title>POS Test</title>
<style>
@page { size: 58mm auto; margin: 0; }
body { font-family: 'Courier New', Courier, monospace; font-size: 13px; width: 54mm; margin: 0 auto; padding: 3mm 1mm; color: #000; line-height: 1.3; }
.center { text-align: center; }
.big { font-size: 18px; font-weight: bold; }
.huge { font-size: 22px; font-weight: bold; text-align: center; }
.sep { border-top: 1px dashed #000; margin: 4px 0; }
@media print { .no-print { display: none !important; } }
</style></head>
<body>
${bodyHtml}
<div class="no-print" style="margin-top:12px;text-align:center"><button onclick="window.print()" style="padding:8px 16px;background:#16A34A;color:#fff;border:none;border-radius:6px">Imprimir</button></div>
<script>
window.onafterprint = function(){
  try { if (window.frameElement) { window.frameElement.dispatchEvent(new Event('afterprint-done')); } } catch(e){}
};
setTimeout(function(){ try { window.print(); } catch(e){ window.parent && window.parent.postMessage({ pos_diag_error: String(e.message||e) }, '*'); } }, ${autoPrintMs});
</script>
</body></html>`;

const tplPlain = () =>
  baseHtml(`
<div class="center">ESTACIONAMIENTO GLANDYMAR</div>
<div class="center">TEST PRINT</div>
<div class="center">123456</div>
`);

const tplFormatted = () =>
  baseHtml(`
<div class="center big">ESTACIONAMIENTO GLANDYMAR</div>
<div class="sep"></div>
<div>TICKET: TEST123</div>
<div>PATENTE: <span class="big">TEST</span></div>
<div>ESPACIO: <span class="big">A1</span></div>
<div class="sep"></div>
<div class="huge">TOTAL: $600</div>
<div class="sep"></div>
<div class="center">Test formato OK</div>
`);

const tplBarcode = () =>
  `<!DOCTYPE html><html><head><meta charset="utf-8"><title>POS Test Barcode</title>
<style>
@page { size: 58mm auto; margin: 0; }
body { font-family: 'Courier New', Courier, monospace; font-size: 13px; width: 54mm; margin: 0 auto; padding: 3mm 1mm; color: #000; line-height: 1.3; }
.center { text-align: center; }
.sep { border-top: 1px dashed #000; margin: 4px 0; }
#barcode { width: 100%; height: 42px; }
@media print { .no-print { display: none !important; } }
</style>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
</head>
<body>
<div class="center">GLANDYMAR · TEST C</div>
<div class="sep"></div>
<svg id="barcode"></svg>
<div class="center">TICKET-TEST-C</div>
<div class="sep"></div>
<div class="no-print" style="margin-top:12px;text-align:center"><button onclick="window.print()" style="padding:8px 16px;background:#16A34A;color:#fff;border:none;border-radius:6px">Imprimir</button></div>
<script>
try {
  JsBarcode("#barcode", "TICKETTESTC", { format:"CODE128", width:1.6, height:38, displayValue:false, margin:4 });
} catch(e) {
  var c = document.getElementById('barcode');
  if (c) c.outerHTML = '<div style="text-align:center;font-family:monospace;font-size:13px;padding:4px;border:1px solid #000">TICKETTESTC</div>';
}
window.onafterprint = function(){ try { if (window.frameElement) { window.frameElement.dispatchEvent(new Event('afterprint-done')); } } catch(e){} };
setTimeout(function(){ try { window.print(); } catch(e){ window.parent && window.parent.postMessage({ pos_diag_error: String(e.message||e) }, '*'); } }, 700);
<\/script>
</body></html>`;

// ============== COMPONENT ==============

export default function PosDiagnostics() {
  const [env, setEnv] = useState(() => detectEnv());
  const [deviceMode, setDeviceMode] = useState('desktop');
  const [log, setLog] = useState(() => readLog());
  const [busy, setBusy] = useState(null); // 'A' | 'B' | 'C' | null
  const iframeRef = useRef(null);
  const pendingTestRef = useRef(null);

  useEffect(() => {
    settingsApi.get()
      .then(r => setDeviceMode(r.data?.device_mode || 'desktop'))
      .catch(() => {});
  }, []);

  // Listen for global print errors bubbled from the iframe
  useEffect(() => {
    const onMsg = (e) => {
      if (e.data?.pos_diag_error && pendingTestRef.current) {
        finalize(pendingTestRef.current, false, e.data.pos_diag_error);
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanupIframe = useCallback(() => {
    if (iframeRef.current) {
      try { document.body.removeChild(iframeRef.current); } catch {}
      iframeRef.current = null;
    }
  }, []);

  const finalize = useCallback((test, ok, errMsg = '') => {
    const entry = {
      ts: new Date().toISOString(),
      test,
      status: ok ? 'completed' : 'failed',
      error: errMsg || null,
      device_mode: deviceMode,
      browser: `${env.browser} ${env.version}`,
      android: env.android_version || (env.android ? 'yes' : 'no'),
      pwa: env.pwa_standalone,
    };
    setLog(appendLog(entry));
    setBusy(null);
    pendingTestRef.current = null;
    // Cleanup safety: remove iframe 5s after final event
    setTimeout(cleanupIframe, 5000);
  }, [deviceMode, env, cleanupIframe]);

  const runTest = useCallback((test, html) => {
    if (busy) return;
    setBusy(test);
    pendingTestRef.current = test;

    // Reuse / replace the single hidden iframe
    cleanupIframe();
    const iframe = document.createElement('iframe');
    iframe.setAttribute('data-testid', 'pos-diag-print-iframe');
    iframe.style.cssText = 'position:fixed;top:0;left:0;width:80mm;height:100vh;border:none;opacity:0;pointer-events:none;z-index:-1;';
    document.body.appendChild(iframe);
    iframeRef.current = iframe;

    let settled = false;
    const onDone = () => {
      if (settled) return;
      settled = true;
      finalize(test, true, '');
    };

    iframe.addEventListener('afterprint-done', onDone);

    try {
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();
    } catch (e) {
      settled = true;
      finalize(test, false, e.message || 'doc.write failed');
      return;
    }

    // Hard timeout (user cancels dialog or print never returns)
    setTimeout(() => {
      if (!settled) {
        settled = true;
        finalize(test, false, 'Timeout: dialog cancelado o sin respuesta');
      }
    }, 30000);
  }, [busy, cleanupIframe, finalize]);

  const clearLog = () => {
    try { localStorage.removeItem(LOG_KEY); } catch {}
    setLog([]);
  };

  const refreshEnv = () => setEnv(detectEnv());

  const last = log[0];
  const InfoRow = ({ label, value, mono = false, status }) => (
    <div className="flex justify-between items-center py-1.5 border-b border-[#27272A] last:border-0">
      <span className="text-[#A1A1AA] text-xs uppercase tracking-wide">{label}</span>
      <span className={`text-white text-sm ${mono ? 'font-mono' : ''} ${status === 'ok' ? 'text-[#4ADE80]' : ''} ${status === 'warn' ? 'text-[#FCD34D]' : ''} ${status === 'err' ? 'text-[#F87171]' : ''}`}>
        {value}
      </span>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="pos-diagnostics-panel">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1C1C1E] to-[#141414] border border-[#27272A] rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-[#16A34A]/10 rounded-lg">
              <Printer className="w-5 h-5 text-[#4ADE80]" weight="bold" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">Diagnóstico POS</h2>
              <p className="text-[#A1A1AA] text-xs">Pruebas progresivas de impresión - no afecta datos productivos</p>
            </div>
          </div>
          <button onClick={refreshEnv} className="p-2 bg-[#27272A] rounded-lg hover:bg-[#3F3F46]" data-testid="refresh-env-btn" title="Refrescar info">
            <ArrowsClockwise className="w-4 h-4 text-white" weight="bold" />
          </button>
        </div>
      </div>

      {/* Environment Card */}
      <div className="bg-[#141414] border border-[#27272A] rounded-2xl p-4" data-testid="pos-diag-env-card">
        <h3 className="text-white font-bold text-sm mb-3 uppercase tracking-wider">Entorno detectado</h3>
        <div className="space-y-0">
          <InfoRow label="Navegador" value={`${env.browser} ${env.version}`} />
          <InfoRow label="Android" value={env.android_version || (env.android ? 'Android (versión desconocida)' : 'No Android')} status={env.android ? 'ok' : 'warn'} />
          <InfoRow label="Dispositivo" value={env.mobile ? 'Móvil/POS' : 'Desktop'} />
          <InfoRow label="PWA Standalone" value={env.pwa_standalone ? 'Sí (instalada)' : 'No (en navegador)'} status={env.pwa_standalone ? 'ok' : 'warn'} />
          <InfoRow label="Web Bluetooth" value={env.web_bluetooth ? 'Soportado' : 'No soportado'} status={env.web_bluetooth ? 'ok' : 'warn'} />
          <InfoRow label="window.print()" value={env.window_print ? 'Disponible' : 'No disponible'} status={env.window_print ? 'ok' : 'err'} />
          <InfoRow label="Modo de dispositivo" value={deviceMode} mono />
          <InfoRow label="Pantalla" value={env.screen} mono />
          <InfoRow label="Viewport" value={env.viewport} mono />
          <InfoRow label="Conexión" value={env.online ? 'Online' : 'Offline'} status={env.online ? 'ok' : 'err'} />
          <InfoRow label="Última prueba" value={last ? `${last.test} · ${last.status}` : 'Sin pruebas'} status={last ? (last.status === 'completed' ? 'ok' : 'err') : undefined} />
          <InfoRow label="Último timestamp" value={last ? new Date(last.ts).toLocaleString('es-CL') : '—'} mono />
          {last?.error && <InfoRow label="Último error" value={last.error} status="err" />}
        </div>
        <details className="mt-3">
          <summary className="text-[#A1A1AA] text-xs cursor-pointer hover:text-white">User Agent completo</summary>
          <p className="text-[10px] text-[#71717A] font-mono break-all mt-1 p-2 bg-black/40 rounded">{env.user_agent}</p>
        </details>
      </div>

      {/* Test Buttons */}
      <div className="bg-[#141414] border border-[#27272A] rounded-2xl p-4">
        <h3 className="text-white font-bold text-sm mb-3 uppercase tracking-wider">Pruebas de impresión</h3>
        <p className="text-[#A1A1AA] text-xs mb-4">
          Ejecuta en orden A → B → C. Si A falla, el problema es el servicio de impresión Android. Si A funciona pero B falla, es el formato/CSS. Si A+B funcionan pero C falla, es el código de barras.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <button
            onClick={() => runTest('A', tplPlain())}
            disabled={busy !== null}
            className="flex flex-col items-start p-4 bg-[#1C1C1E] hover:bg-[#27272A] border border-[#27272A] rounded-xl text-left disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="pos-test-plain-btn"
          >
            <div className="flex items-center gap-2 mb-2">
              <TextAa className="w-5 h-5 text-[#4ADE80]" weight="bold" />
              <span className="text-white font-bold">Prueba A</span>
              {busy === 'A' && <span className="text-[#FCD34D] text-xs">Imprimiendo...</span>}
            </div>
            <span className="text-[#A1A1AA] text-xs">Texto plano: 3 líneas, sin formato</span>
            <span className="text-[#71717A] text-[10px] mt-1">Verifica que el servicio de impresión responda</span>
          </button>

          <button
            onClick={() => runTest('B', tplFormatted())}
            disabled={busy !== null}
            className="flex flex-col items-start p-4 bg-[#1C1C1E] hover:bg-[#27272A] border border-[#27272A] rounded-xl text-left disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="pos-test-formatted-btn"
          >
            <div className="flex items-center gap-2 mb-2">
              <Article className="w-5 h-5 text-[#FCD34D]" weight="bold" />
              <span className="text-white font-bold">Prueba B</span>
              {busy === 'B' && <span className="text-[#FCD34D] text-xs">Imprimiendo...</span>}
            </div>
            <span className="text-[#A1A1AA] text-xs">Ticket formateado con separadores</span>
            <span className="text-[#71717A] text-[10px] mt-1">Verifica CSS/anchos de página 58mm</span>
          </button>

          <button
            onClick={() => runTest('C', tplBarcode())}
            disabled={busy !== null}
            className="flex flex-col items-start p-4 bg-[#1C1C1E] hover:bg-[#27272A] border border-[#27272A] rounded-xl text-left disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="pos-test-barcode-btn"
          >
            <div className="flex items-center gap-2 mb-2">
              <Barcode className="w-5 h-5 text-[#60A5FA]" weight="bold" />
              <span className="text-white font-bold">Prueba C</span>
              {busy === 'C' && <span className="text-[#FCD34D] text-xs">Imprimiendo...</span>}
            </div>
            <span className="text-[#A1A1AA] text-xs">Código de barras CODE128 + texto</span>
            <span className="text-[#71717A] text-[10px] mt-1">Verifica render de gráficos en el ticket</span>
          </button>
        </div>

        {busy && (
          <div className="mt-3 p-3 bg-[#422006] border border-[#D97706]/30 rounded-lg flex items-center gap-2" data-testid="pos-diag-busy">
            <Warning className="w-4 h-4 text-[#FCD34D]" weight="bold" />
            <p className="text-[#FCD34D] text-xs">Prueba {busy} en curso. Confirma el diálogo del POSPrinter en el dispositivo.</p>
          </div>
        )}
      </div>

      {/* Log */}
      <div className="bg-[#141414] border border-[#27272A] rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-bold text-sm uppercase tracking-wider">Historial de pruebas ({log.length})</h3>
          {log.length > 0 && (
            <button onClick={clearLog} className="flex items-center gap-1 px-2 py-1 bg-[#27272A] hover:bg-[#3F3F46] rounded text-xs text-white" data-testid="clear-log-btn">
              <Trash className="w-3 h-3" weight="bold" /> Limpiar
            </button>
          )}
        </div>
        {log.length === 0 ? (
          <p className="text-[#71717A] text-sm text-center py-6">Sin pruebas registradas aún</p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="pos-diag-log-list">
            {log.map((e, i) => (
              <div key={i} className="flex items-start gap-3 p-2.5 bg-[#1C1C1E] border border-[#27272A] rounded-lg">
                {e.status === 'completed' ? (
                  <CheckCircle className="w-5 h-5 text-[#4ADE80] shrink-0 mt-0.5" weight="bold" />
                ) : (
                  <XCircle className="w-5 h-5 text-[#F87171] shrink-0 mt-0.5" weight="bold" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-bold text-sm">Prueba {e.test}</span>
                    <span className={`text-xs font-semibold ${e.status === 'completed' ? 'text-[#4ADE80]' : 'text-[#F87171]'}`}>
                      {e.status === 'completed' ? 'OK' : 'FALLÓ'}
                    </span>
                    <span className="text-[#71717A] text-xs font-mono">{new Date(e.ts).toLocaleString('es-CL')}</span>
                  </div>
                  <p className="text-[#A1A1AA] text-xs mt-0.5">
                    {e.browser} · Android {e.android} · {e.pwa ? 'PWA' : 'Browser'} · modo {e.device_mode}
                  </p>
                  {e.error && <p className="text-[#F87171] text-xs mt-1 break-words">⚠ {e.error}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recommendation */}
      <div className="bg-[#0C1F2C] border border-[#1E3A5F] rounded-2xl p-4" data-testid="pos-diag-guidance">
        <h3 className="text-[#60A5FA] font-bold text-sm uppercase tracking-wider mb-2">Cómo leer los resultados</h3>
        <ul className="text-[#A1A1AA] text-xs space-y-1.5 list-disc list-inside">
          <li><span className="text-white font-semibold">Si A falla:</span> el servicio Android (POSPrinter) está fallando en general. Revisa que la impresora integrada esté seleccionada como predeterminada.</li>
          <li><span className="text-white font-semibold">Si A funciona pero B falla:</span> el formato/CSS es el problema (probable ancho de página &gt; 58mm o estilos no soportados).</li>
          <li><span className="text-white font-semibold">Si A y B funcionan pero C falla:</span> el render de barcode/QR causa el error. Habría que usar SVG inline o pasar a impresión nativa.</li>
          <li><span className="text-white font-semibold">Si las 3 funcionan:</span> el ticket original es demasiado grande/complejo. Simplificar el template productivo.</li>
        </ul>
      </div>
    </div>
  );
}
